import React, { useState, useEffect } from 'react';
import { Modal } from '../ui';
import { SectionConfig } from '../../types/schema';

interface Props {
  isOpen: boolean;
  section: { key: string; config: SectionConfig } | null; // null = new section
  existingKeys: string[];
  onClose: () => void;
  onSave: (key: string, config: SectionConfig) => void;
  onDelete?: (key: string) => void;
}

const ICON_OPTIONS = [
  { value: 'info', label: 'Info' },
  { value: 'document', label: 'Document' },
  { value: 'user', label: 'Utilisateur' },
  { value: 'building', label: 'Entreprise' },
  { value: 'money', label: 'Finance' },
  { value: 'calendar', label: 'Date' },
  { value: 'location', label: 'Localisation' },
  { value: 'settings', label: 'Paramètres' },
];

export const SectionFormModal: React.FC<Props> = ({
  isOpen,
  section,
  existingKeys,
  onClose,
  onSave,
  onDelete
}) => {
  const isEdit = !!section;
  const [key, setKey] = useState('');
  const [labelFr, setLabelFr] = useState('');
  const [order, setOrder] = useState(0);
  const [icon, setIcon] = useState('info');
  const [error, setError] = useState('');

  useEffect(() => {
    if (section) {
      setKey(section.key);
      setLabelFr(section.config.label_fr);
      setOrder(section.config.order);
      setIcon(section.config.icon || 'info');
    } else {
      // Default for new section
      setKey('');
      setLabelFr('');
      setOrder(existingKeys.length * 10); // Auto-increment by 10
      setIcon('info');
    }
    setError('');
  }, [section, existingKeys, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!key.trim()) {
      setError('La clé est obligatoire');
      return;
    }

    if (!labelFr.trim()) {
      setError('Le label est obligatoire');
      return;
    }

    // Check for duplicate key (only for new sections)
    if (!isEdit && existingKeys.includes(key)) {
      setError('Cette clé existe déjà');
      return;
    }

    onSave(key, {
      label_fr: labelFr,
      order,
      icon
    });
    onClose();
  };

  const handleDelete = () => {
    if (section && onDelete) {
      if (window.confirm(`Supprimer la section "${section.config.label_fr}" ? Les champs de cette section devront être réassignés.`)) {
        onDelete(section.key);
        onClose();
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Modifier la section' : 'Nouvelle section'}
      size="sm"
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Clé technique
            </label>
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              disabled={isEdit}
              className="w-full border rounded px-3 py-2 disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="ex: identification_vehicule"
            />
            {isEdit && (
              <p className="text-xs text-gray-500 mt-1">La clé ne peut pas être modifiée</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label (FR)
            </label>
            <input
              type="text"
              value={labelFr}
              onChange={(e) => setLabelFr(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="ex: Identification du véhicule"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ordre d'affichage
            </label>
            <input
              type="number"
              value={order}
              onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icône
            </label>
            <select
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {ICON_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-between mt-6 pt-4 border-t">
          <div>
            {isEdit && onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded"
              >
                Supprimer
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-600"
            >
              {isEdit ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default SectionFormModal;
